import { Component, h, Host, Element as HostElement, Listen, Prop, State } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/menu?tab=designer).
 *
 * @slot default - Slot para o conteúdo principal do menu (menu-list e menu-item).
 * @slot header - Slot para o cabeçalho do menu, exibido apenas em modo não contextual.
 * @slot logo - Slot para logotipos de parceiros no rodapé.
 * @slot link - Slot para links externos no rodapé (use br-menu-link).
 * @slot social - Slot para ícones de redes sociais no rodapé.
 * @slot info - Slot para informações adicionais no rodapé.
 *
 * @part footer - Parte para o contêiner principal do rodapé do menu, agrupando logos, links, redes sociais e informações.
 * @part logo - Parte para a área de logotipos de parceiros no rodapé.
 * @part link - Parte para links úteis ou navegação secundária no rodapé.
 * @part social - Parte para ícones de redes sociais no rodapé.
 * @part info - Parte para informações adicionais, como licença ou direitos autorais.
 */
@Component({
  tag: 'br-menu',
  styleUrl: 'menu.scss',
  shadow: true,
  formAssociated: true,
})
export class Menu {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @HostElement() private el: HTMLElement

  /**
   * Classes CSS para definir breakpoints responsivos do menu.
   * Utilize esta propriedade para controlar a largura do menu em diferentes tamanhos de tela.
   */
  // Por padrão não aplica colunas; quando contextual e sem valor definido, usa 'col-sm-4 col-lg-3'
  @Prop() breakpoints: string = ''

  /**
   * Define se o menu deve usar comportamento push (sempre visível em telas maiores).
   * Quando ativado, o menu permanece fixo na lateral e empurra o conteúdo principal.
   */
  @Prop() push: boolean = false

  /**
   * Define se o menu deve usar comportamento contextual (aparece na parte inferior).
   * Quando ativado, o menu é posicionado na parte inferior da tela em dispositivos móveis.
   */
  @Prop() contextual: boolean = false

  /**
   * Define a densidade dos itens do menu, alterando o espaçamento interno.
   * - `small` (densidade alta): itens mais compactos
   * - `medium` (padrão): equilíbrio entre economia de espaço e separação
   * - `large` (densidade baixa): maior espaçamento (recomendado em touch)
   */
  @Prop({ reflect: true }) density: 'large' | 'medium' | 'small' = 'medium'

  /**
   * Título da seção de redes sociais exibida no rodapé do menu.
   * Utilize esta propriedade para personalizar o texto que aparece acima dos ícones sociais.
   */
  @Prop({ reflect: true }) socialTitle: string = 'Redes Sociais'

  /**
   * Rótulo do botão trigger do menu contextual.
   * Exibido apenas em modo contextual e em telas menores (mobile).
   */
  @Prop() contextualLabel: string = 'Menu Contextual'

  // Define se o menu está aberto/visível ou fechado
  @State() active: boolean = false

  // Indica se o menu está exibindo um submenu (drill-down ativo)
  @State() inSubmenu: boolean = false

  // Representa o nível atual de navegação no drill-down (0 para o nível raiz)
  @State() currentLevel: number = 0

  // Controla quais menus drop-down estão expandidos no modo acordeão
  @State() expandedDropMenus: { [key: string]: boolean } = {}

  // Pilha de navegação para controle do drill-down e navegação de volta
  @State() navigationStack: Array<{ element: HTMLElement; label: string; id: string }> = []

  // Armazena o elemento que tinha foco antes do menu abrir (para restaurar o foco ao fechar)
  private previouslyFocusedElement: HTMLElement | null = null

  // IDs dos requestAnimationFrame para cancelamento no cleanup
  private rafIds: number[] = []

  componentDidLoad() {
    // Garante que apenas um item tenha active no carregamento inicial
    this.ensureSingleActiveItem()
  }

  /**
   * Método do ciclo de vida do componente.
   * Este método é chamado quando o componente é desconectado do DOM.
   * Realiza limpeza de recursos para evitar vazamento de memória.
   */
  disconnectedCallback() {
    // Cancela todos os requestAnimationFrame pendentes
    this.rafIds.forEach((id) => cancelAnimationFrame(id))
    this.rafIds = []
  }

  private ensureSingleActiveItem() {
    const allMenuItems = this.el.querySelectorAll('br-menu-item')
    let firstActiveItem: HTMLElement | null = null

    // Encontra o primeiro item com active e remove dos demais
    for (let i = 0; i < allMenuItems.length; i++) {
      const item = allMenuItems[i] as HTMLElement & { active: boolean; displayMode?: string }

      // Verifica se o item tem active
      if (item.active || item.hasAttribute('active')) {
        // Se é um header de menu-list (folder), remove o active
        const displayMode = item.getAttribute('display-mode')
        if (displayMode && displayMode !== 'none') {
          item.active = false
          item.removeAttribute('active')
          continue
        }

        // Se já encontramos um item ativo antes, remove deste
        if (firstActiveItem) {
          item.active = false
          item.removeAttribute('active')
        } else {
          // É o primeiro item ativo válido
          firstActiveItem = item
          item.active = true
        }
      }
    }
  }

  @Listen('click', { capture: true })
  handleMenuItemClick(ev: Event) {
    // Busca o menu-item mais próximo do alvo do clique
    const path = ev.composedPath()
    let menuItemHost: HTMLElement | null = null

    // Percorre o caminho do evento para encontrar o br-menu-item
    for (const element of path) {
      if (element instanceof HTMLElement && element.tagName === 'BR-MENU-ITEM') {
        menuItemHost = element
        break
      }
      // Para quando chegar no host do menu
      if (element === this.el) break
    }

    if (!menuItemHost || !this.el.contains(menuItemHost)) return

    // Ignora se for um header de menu-list (folder/dropdown que tem filhos)
    // Menus que expandem/têm submenus não devem receber classe active
    const parentMenuList = menuItemHost.parentElement
    if (parentMenuList?.tagName === 'BR-MENU-LIST') {
      // Se é filho direto de br-menu-list, verifica se é o header (primeiro br-menu-item)
      const header = parentMenuList.shadowRoot?.querySelector('br-menu-item')
      if (header === menuItemHost) {
        // É um header de folder, não deve receber active
        return
      }
    }

    // Também verifica via atributo display-mode
    const displayMode = menuItemHost.getAttribute('display-mode')
    if (displayMode && displayMode !== 'none') {
      return
    }

    // Remove active de todos os menu-items
    const allMenuItems = this.el.querySelectorAll('br-menu-item')
    for (let i = 0; i < allMenuItems.length; i++) {
      const item = allMenuItems[i] as HTMLElement & { active: boolean }
      item.active = false
    }

    // Marca o item clicado como ativo
    ;(menuItemHost as HTMLElement & { active: boolean }).active = true
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(ev: KeyboardEvent) {
    // Permite ativar o botão contextual com Enter ou Espaço quando o menu está fechado
    if (!this.active && this.contextual && (ev.key === 'Enter' || ev.key === ' ')) {
      const target = ev.target as HTMLElement
      const isContextualButton = target?.closest?.('#contextual-trigger') || target?.id === 'contextual-trigger'

      if (isContextualButton) {
        ev.preventDefault()
        this.toggleMenu()
        return
      }
    }

    if (!this.active) return
    const targetEl = ev.target as HTMLElement | null
    const isFormField =
      targetEl &&
      (targetEl.tagName === 'INPUT' ||
        targetEl.tagName === 'TEXTAREA' ||
        targetEl.tagName === 'SELECT' ||
        targetEl.isContentEditable)

    // Focus trap: gerencia Tab/Shift+Tab para manter o foco dentro do menu
    if (ev.key === 'Tab') {
      const focusableElements = this.getFocusableElements()
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const deepActive = this.getDeepActiveElement()

      // Se Tab sem Shift e está no último elemento, volta para o primeiro
      if (!ev.shiftKey && deepActive === lastElement) {
        ev.preventDefault()
        firstElement.focus()
        return
      }

      // Se Shift+Tab e está no primeiro elemento, vai para o último
      if (ev.shiftKey && deepActive === firstElement) {
        ev.preventDefault()
        lastElement.focus()
        return
      }
    }

    // Voltar/fechar
    if (this.inSubmenu && (ev.key === 'ArrowLeft' || ev.key === 'Backspace' || ev.key === 'Escape')) {
      ev.preventDefault()
      this.goBack()
      return
    }
    if (!this.inSubmenu && ev.key === 'Escape') {
      ev.preventDefault()
      this.closeMenu()
      return
    }

    // Navegação global por setas (fallback para todos os tipos de menu)
    if (!isFormField && (ev.key === 'ArrowDown' || ev.key === 'ArrowUp')) {
      const deepActive = this.getDeepActiveElement()
      if (deepActive == null || !this.isNodeWithinHost(deepActive)) return
      ev.preventDefault()
      ev.stopPropagation()

      // No modo contextual ou quando não há drill-down ativo, navega por todos os itens visíveis
      const useGlobalNavigation = this.contextual || !this.inSubmenu
      const targets = useGlobalNavigation ? this.getAllVisibleTargets() : this.getScopedTargets(deepActive)
      if (targets.length === 0) return

      let index = targets.findIndex((t) => t === deepActive || (deepActive instanceof Node && t.contains(deepActive)))
      if (index === -1) index = 0
      const nextIndex =
        ev.key === 'ArrowDown' ? (index + 1) % targets.length : (index - 1 + targets.length) % targets.length
      targets[nextIndex].focus()
    }
  }

  @Listen('navigateToSubmenu')
  handleNavigateToSubmenu(ev: CustomEvent<{ element: HTMLElement; label: string }>) {
    const { element, label } = ev.detail
    const itemId = element.getAttribute('data-menu-id') || `menu-${Date.now()}`

    // Obter o nível real do menu do elemento
    const menuLevel = parseInt(element.getAttribute('menu-level') || '0', 10)

    // Aplicar a regra principal: comportamento por nível
    if (menuLevel === 0) {
      // Camada 1: Comportamento de acordeão
      this.toggleDropMenu(itemId)
    } else {
      // Camada 2+: Comportamento drill-down
      this.navigateToLevel({ element, label, id: itemId })
    }
  }

  @Listen('folderToggled')
  handleFolderToggled(ev: CustomEvent<boolean>) {
    const element = ev.target as HTMLElement
    const itemId = element.getAttribute('data-menu-id') || `menu-${Date.now()}`

    if (ev.detail) {
      // Se o folder foi expandido, fecha todos os outros no nível 0
      this.collapseOtherFolders(itemId)
    }
  }

  @Listen('brMenuHeaderClose')
  handleMenuHeaderClose() {
    this.closeMenu()
  }

  /**
   * Alterna o estado expandido de um menu drop-down no modo acordeão.
   *
   * Este método recebe o identificador único (`itemId`) de um menu do tipo drop-down (nível 0)
   * e inverte o seu estado de expansão dentro do objeto `expandedDropMenus`.
   *
   * Se o menu estiver atualmente expandido, ele será recolhido; se estiver recolhido, será expandido.
   * O spread operator (`...this.expandedDropMenus`) garante que os estados dos outros menus sejam preservados.
   *
   * @param itemId - Identificador único do menu drop-down a ser alternado.
   */
  private toggleDropMenu(itemId: string) {
    this.expandedDropMenus = {
      ...this.expandedDropMenus,
      [itemId]: !this.expandedDropMenus[itemId],
    }
  }

  /**
   * Recolhe todos os folders do nível 0, exceto o especificado.
   * Garante que apenas um folder possa estar expandido por vez no comportamento acordeão.
   *
   * @param activeItemId - ID do folder que deve permanecer expandido
   */
  private collapseOtherFolders(activeItemId: string) {
    // Encontra todos os menu-list no nível 0
    const level0MenuLists = this.el.querySelectorAll('br-menu-list[menu-level="0"]')

    level0MenuLists.forEach((menuList) => {
      const element = menuList as HTMLElement
      const itemId = element.getAttribute('data-menu-id')

      // Se é um folder diferente do ativo, força o recolhimento
      if (itemId && itemId !== activeItemId) {
        // Força o estado recolhido no menu-list
        ;(element as HTMLElement & { expanded: boolean }).expanded = false
        // Remove do estado de expandedDropMenus
        if (this.expandedDropMenus[itemId]) {
          this.expandedDropMenus = {
            ...this.expandedDropMenus,
            [itemId]: false,
          }
        }
      }
    })
  }

  /**
   * Navega para um nível específico do menu (drill-down).
   *
   * Este método é chamado quando o usuário interage com um item de menu de nível 2 ou superior,
   * ou seja, quando o comportamento esperado é de drill-down (aprofundamento em submenus).
   *
   * Funcionamento:
   * 1. Adiciona o item atual à pilha de navegação (`navigationStack`), permitindo que o usuário volte para níveis anteriores.
   * 2. Obtém o nível do menu a partir do atributo `menu-level` do elemento clicado.
   * 3. Atualiza o estado `currentLevel` para refletir o novo nível de profundidade (sempre um nível abaixo do atual).
   * 4. Define `inSubmenu` como `true`, indicando que o usuário está navegando em um submenu.
   * 5. Oculta todos os itens do menu, exceto o submenu selecionado, para garantir foco e clareza na navegação.
   * 6. Marca o elemento ativo como "drill active" para controle visual e de acessibilidade.
   *
   * @param item - Objeto contendo o elemento do menu, seu rótulo e identificador único.
   */
  private navigateToLevel(item: { element: HTMLElement; label: string; id: string }) {
    // Adiciona o item à pilha de navegação para possibilitar o retorno (back)
    this.navigationStack.push(item)

    // Obtém o nível do menu a partir do atributo do elemento
    const menuLevel = parseInt(item.element.getAttribute('menu-level') || '0', 10)

    // Atualiza o nível atual para o próximo nível após o elemento clicado
    this.currentLevel = menuLevel + 1

    // Indica que estamos em um submenu (drill-down ativo)
    this.inSubmenu = true

    // Oculta todos os menus, exceto o submenu selecionado
    this.hideAllExcept(item.element)

    // Marca o submenu como ativo para controle visual e de acessibilidade
    this.setDrillActive(item.element, true)
    // Após entrar no submenu, foca no primeiro item visível
    const rafId = requestAnimationFrame(() => this.focusFirstChildOfList(item.element))
    this.rafIds.push(rafId)
  }

  private focusFirstChildOfList(listEl: HTMLElement) {
    const slotEl = listEl.shadowRoot?.querySelector('ul slot') as HTMLSlotElement | null
    const assigned = slotEl?.assignedElements?.() || []
    for (const el of assigned) {
      if (el.tagName === 'BR-MENU-ITEM') {
        const anchor = (el as HTMLElement).shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(el) && this.isVisibleElement(anchor)) {
          anchor.focus()
          return
        }
      } else if (el.tagName === 'BR-MENU-LIST') {
        const header = (el as HTMLElement).shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
        const anchor = header?.shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(el) && this.isVisibleElement(anchor)) {
          anchor.focus()
          return
        }
      }
    }
  }

  private toggleMenu() {
    this.active = !this.active
    if (this.active) {
      // Armazena o elemento que estava com foco antes de abrir o menu
      this.previouslyFocusedElement = this.getDeepActiveElement() as HTMLElement | null
      // Foca no primeiro elemento visível do menu
      const rafId = requestAnimationFrame(() => this.focusFirstVisibleTarget())
      this.rafIds.push(rafId)
    } else {
      // Se está fechando, chama closeMenu para fazer a limpeza completa
      this.closeMenu()
    }
  }

  private closeMenu() {
    this.active = false
    this.inSubmenu = false
    this.resetNavigation()

    // Restaura o foco para o elemento que estava focado antes do menu abrir
    if (this.previouslyFocusedElement) {
      const rafId = requestAnimationFrame(() => {
        this.previouslyFocusedElement?.focus()
        this.previouslyFocusedElement = null
      })
      this.rafIds.push(rafId)
    } else {
      // Se não há elemento anterior, foca no botão toggle
      const rafId = requestAnimationFrame(() => {
        const toggleButton = this.contextual
          ? this.el.shadowRoot?.querySelector('#contextual-trigger')
          : this.el.shadowRoot?.querySelector('#toggle-menu')

        if (toggleButton) {
          const button = (toggleButton as HTMLElement).shadowRoot?.querySelector('button') as HTMLElement | null
          button?.focus()
        }
      })
      this.rafIds.push(rafId)
    }
  }

  private resetNavigation() {
    this.navigationStack = []
    this.currentLevel = 0
    this.expandedDropMenus = {}
    this.clearAllDrillMarkers()
    this.showAllMenuItems()
  }

  private isVisibleElement(el: Element | null): el is HTMLElement {
    if (el == null) return false
    const htmlEl = el as HTMLElement
    if (!htmlEl.isConnected) return false
    if (htmlEl.hasAttribute('hidden')) return false
    const rects = htmlEl.getClientRects?.()
    return rects != null && rects.length > 0
  }

  private getTopLevelTargets(): HTMLElement[] {
    const targets: HTMLElement[] = []
    const bodySlot = this.el.shadowRoot?.querySelector('nav.menu-body slot:not([name])') as HTMLSlotElement | null
    const children = bodySlot?.assignedElements?.() || []

    for (const child of children) {
      if (child.tagName === 'BR-MENU-ITEM') {
        if (!this.isVisibleElement(child)) continue
        const anchor = (child as HTMLElement).shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(anchor)) targets.push(anchor)
      } else if (child.tagName === 'BR-MENU-LIST') {
        if (!this.isVisibleElement(child)) continue
        const header = (child as HTMLElement).shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
        const anchor = header?.shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(anchor)) targets.push(anchor)
      }
    }

    return targets
  }

  /**
   * Obtém todos os elementos navegáveis visíveis no menu, incluindo itens dentro de agrupamentos expandidos.
   * Utilizado para navegação global por setas no modo contextual.
   */
  private getAllVisibleTargets(): HTMLElement[] {
    const targets: HTMLElement[] = []
    const bodySlot = this.el.shadowRoot?.querySelector('nav.menu-body slot:not([name])') as HTMLSlotElement | null
    const children = bodySlot?.assignedElements?.() || []

    for (const child of children) {
      this.collectVisibleTargetsFrom(child, targets)
    }

    return targets
  }

  /**
   * Coleta recursivamente todos os targets visíveis de um elemento e seus descendentes.
   * Respeita o estado expandido/recolhido dos agrupamentos.
   */
  private collectVisibleTargetsFrom(element: Element, targets: HTMLElement[]) {
    if (element.tagName === 'BR-MENU-ITEM') {
      if (!this.isVisibleElement(element)) return
      const anchor = (element as HTMLElement).shadowRoot?.querySelector('a') as HTMLElement | null
      if (this.isVisibleElement(anchor)) {
        targets.push(anchor)
      }
    } else if (element.tagName === 'BR-MENU-LIST') {
      if (!this.isVisibleElement(element)) return

      // Adiciona o header do menu-list
      const header = (element as HTMLElement).shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
      const headerAnchor = header?.shadowRoot?.querySelector('a') as HTMLElement | null
      if (this.isVisibleElement(headerAnchor)) {
        targets.push(headerAnchor)
      }

      // Verifica se o menu-list está expandido antes de processar os filhos
      const menuListEl = element as HTMLElement & { expanded: boolean }
      const isExpanded = menuListEl.expanded || menuListEl.hasAttribute('expanded')

      if (isExpanded) {
        // Verifica também se o <ul> está realmente visível (para nível 0 que usa display:none)
        const ulElement = (element as HTMLElement).shadowRoot?.querySelector('ul') as HTMLElement | null
        const isUlVisible = ulElement ? this.isVisibleElement(ulElement) : false

        if (isUlVisible) {
          // Coleta os filhos do slot do menu-list
          const slotEl = ulElement.querySelector('slot') as HTMLSlotElement | null
          const assigned = slotEl?.assignedElements?.() || []
          for (const child of assigned) {
            this.collectVisibleTargetsFrom(child, targets)
          }
        }
      }
    }
  }

  /**
   * Obtém targets com escopo limitado ao menu-list mais próximo (usado em drill-down).
   */
  private getScopedTargets(deepActive: Element): HTMLElement[] {
    const listScope = this.findClosestMenuListFrom(deepActive)
    return listScope ? this.getTargetsForMenuList(listScope) : this.getTopLevelTargets()
  }

  private isNodeWithinHost(node: Element): boolean {
    let current: Element | null | ShadowRoot = node
    while (current != null) {
      if (current === this.el) return true
      if (current instanceof Element && current.parentElement != null) {
        current = current.parentElement
      } else {
        const root = current instanceof Element ? (current.getRootNode() as Document | ShadowRoot) : current
        current = root != null && 'host' in root ? (root as ShadowRoot).host : null
      }
    }
    return false
  }

  private findClosestMenuListFrom(node: Element): HTMLElement | null {
    let current: Element | null | ShadowRoot = node
    while (current != null) {
      if (current instanceof Element && current.tagName === 'BR-MENU-LIST') return current as HTMLElement
      if (current instanceof Element && current.parentElement != null) {
        current = current.parentElement
      } else {
        const root = current instanceof Element ? (current.getRootNode() as Document | ShadowRoot) : current
        current = root != null && 'host' in root ? (root as ShadowRoot).host : null
      }
    }
    return null
  }

  private getTargetsForMenuList(listEl: HTMLElement): HTMLElement[] {
    const targets: HTMLElement[] = []
    const header = listEl.shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
    const headerAnchor = header?.shadowRoot?.querySelector('a') as HTMLElement | null
    if (this.isVisibleElement(header) && this.isVisibleElement(headerAnchor)) targets.push(headerAnchor)

    const slotEl = listEl.shadowRoot?.querySelector('ul slot') as HTMLSlotElement | null
    const assigned = slotEl?.assignedElements?.() || []
    for (const el of assigned) {
      if (el.tagName === 'BR-MENU-ITEM') {
        const anchor = (el as HTMLElement).shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(el) && this.isVisibleElement(anchor)) targets.push(anchor)
      } else if (el.tagName === 'BR-MENU-LIST') {
        const subHeader = (el as HTMLElement).shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
        const subAnchor = subHeader?.shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(el) && this.isVisibleElement(subAnchor)) targets.push(subAnchor)
      }
    }
    return targets
  }

  private focusFirstVisibleTarget() {
    const targets = this.getTopLevelTargets()
    if (targets.length > 0) targets[0].focus()
  }

  /**
   * Coleta recursivamente os elementos focáveis no modo drill-down.
   * Respeita a ordem do DOM e a hierarquia dos elementos.
   * Apenas coleta elementos que não têm o atributo 'hidden'.
   */
  private collectDrillDownTargets(element: HTMLElement, targets: HTMLElement[]) {
    const children = Array.from(element.children)

    for (const child of children) {
      if (child.hasAttribute('hidden')) continue
      if (!this.isVisibleElement(child)) continue

      if (child.tagName === 'BR-MENU-ITEM') {
        const anchor = (child as HTMLElement).shadowRoot?.querySelector('a') as HTMLElement | null
        if (anchor && this.isVisibleElement(anchor)) {
          targets.push(anchor)
        }
      } else if (child.tagName === 'BR-MENU-LIST') {
        // Adiciona o header do menu-list
        const header = (child as HTMLElement).shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
        const headerAnchor = header?.shadowRoot?.querySelector('a') as HTMLElement | null
        if (headerAnchor && this.isVisibleElement(headerAnchor)) {
          targets.push(headerAnchor)
        }

        // Coleta recursivamente os filhos do menu-list
        this.collectDrillDownTargets(child as HTMLElement, targets)
      } else {
        // Para outros elementos, continua a busca recursiva
        this.collectDrillDownTargets(child as HTMLElement, targets)
      }
    }
  }

  /**
   * Obtém todos os elementos focáveis dentro do menu que estão visíveis.
   * Inclui botões, links e outros elementos interativos.
   * No modo contextual, inclui itens dentro de agrupamentos expandidos.
   */
  private getFocusableElements(): HTMLElement[] {
    const focusable: HTMLElement[] = []
    const menuPanel = this.el.shadowRoot?.querySelector('.menu-panel')
    if (!menuPanel) return focusable

    // Adiciona o botão trigger (toggle/contextual) como primeiro elemento com foco
    const triggerButton = this.contextual
      ? this.el.shadowRoot?.querySelector('#contextual-trigger')
      : this.el.shadowRoot?.querySelector('#toggle-menu')

    if (triggerButton) {
      const button = (triggerButton as HTMLElement).shadowRoot?.querySelector('button') as HTMLElement | null
      if (button && this.isVisibleElement(button)) {
        focusable.push(button)
      }
    }

    // Busca o botão de voltar se estiver em submenu
    const backButton = this.el.shadowRoot?.querySelector('.back-btn') as HTMLElement | null
    if (backButton && this.isVisibleElement(backButton)) {
      focusable.push(backButton)
    }

    // No modo contextual ou quando não há drill-down, usa navegação global que respeita estado expandido
    if (this.contextual || !this.inSubmenu) {
      const targets = this.getAllVisibleTargets()
      focusable.push(...targets)
    } else {
      // Em drill-down, coleta elementos na ordem do DOM respeitando a hierarquia
      // O método hideAllExcept() já controla a visibilidade via atributo 'hidden'
      this.collectDrillDownTargets(this.el, focusable)
    }

    return focusable
  }

  // Navegação por setas é tratada via listener global de teclado

  private getDeepActiveElement(root: Document | ShadowRoot = document): Element | null {
    const active = root.activeElement as Element | null
    const shadowRoot = active && 'shadowRoot' in active ? (active.shadowRoot as ShadowRoot | null) : null
    if (shadowRoot && shadowRoot.activeElement) return this.getDeepActiveElement(shadowRoot)
    return active
  }

  /**
   * Oculta todos os elementos do menu, exceto o elemento ativo e sua cadeia de ancestrais,
   * garantindo que apenas o submenu relevante esteja visível durante a navegação drill-down.
   *
   * Funcionamento detalhado:
   * 1. Oculta todos os elementos <br-menu-list> e <br-menu-item> do menu, aplicando o atributo 'hidden'.
   *    Isso garante que nada além do caminho de navegação atual fique visível.
   * 2. Remove o atributo 'drill-ancestor' de quaisquer listas marcadas anteriormente, limpando o estado visual.
   * 3. Remove o atributo 'hidden' do elemento ativo (o submenu ou item selecionado), tornando-o visível.
   * 4. Percorre a cadeia de ancestrais do elemento ativo:
   *    - Para cada ancestral do tipo <br-menu-list> ou <br-menu-item>, remove o 'hidden' para garantir visibilidade.
   *    - Para ancestrais <br-menu-list> (exceto o próprio ativo), adiciona o atributo 'drill-ancestor' para permitir
   *      estilização diferenciada (por exemplo, ocultar cabeçalhos mas manter o conteúdo visível).
   *    - Interrompe o loop ao chegar no elemento host do componente.
   * 5. Exibe todos os filhos diretos do elemento ativo que sejam <br-menu-list> ou <br-menu-item>,
   *    removendo o 'hidden' para mostrar as opções do submenu.
   * 6. Exibe também filhos que estejam dentro de um <ul> (estrutura comum em subníveis),
   *    garantindo que todos os itens relevantes do submenu estejam visíveis.
   *
   * Este método é fundamental para a experiência de navegação drill-down, pois controla
   * exatamente quais partes do menu são exibidas ao usuário em cada etapa da navegação.
   *
   * @param activeElement O elemento <br-menu-list> ou <br-menu-item> que representa o submenu/item atualmente ativo.
   */
  private hideAllExcept(activeElement: HTMLElement) {
    // 1. Oculta todos os elementos do menu
    const allMenuElements = this.el.querySelectorAll('br-menu-list, br-menu-item')
    allMenuElements.forEach((element: Element) => {
      ;(element as HTMLElement).setAttribute('hidden', '')
    })

    // 2. Limpa marcadores de ancestrais anteriores
    this.el.querySelectorAll('br-menu-list[drill-ancestor]').forEach((e) => {
      ;(e as HTMLElement).removeAttribute('drill-ancestor')
    })

    // 3. Garante que o elemento ativo esteja visível
    activeElement.removeAttribute('hidden')

    // 4. Exibe a cadeia de ancestrais do elemento ativo
    let ancestor: HTMLElement | null = activeElement.parentElement as HTMLElement | null
    while (ancestor !== null) {
      const tag = ancestor.tagName
      if (tag === 'BR-MENU-LIST' || tag === 'BR-MENU-ITEM') {
        ancestor.removeAttribute('hidden')
      }
      if (tag === 'BR-MENU-LIST' && ancestor !== activeElement) {
        ancestor.setAttribute('drill-ancestor', '')
      }
      if (ancestor === this.el) break
      ancestor = ancestor.parentElement as HTMLElement | null
    }

    // 5. Exibe filhos diretos do elemento ativo
    const directChildren = activeElement.querySelectorAll(':scope > br-menu-list, :scope > br-menu-item')
    directChildren.forEach((child: Element) => {
      ;(child as HTMLElement).removeAttribute('hidden')
    })

    // 6. Exibe filhos dentro de <ul> do elemento ativo
    const listChildren = activeElement.querySelectorAll(':scope > ul br-menu-list, :scope > ul br-menu-item')
    listChildren.forEach((child: Element) => {
      ;(child as HTMLElement).removeAttribute('hidden')
    })
  }

  /**
   * Exibe todos os itens e listas do menu, removendo o atributo 'hidden' de cada um.
   *
   * Esta função é utilizada para restaurar a visualização completa do menu,
   * normalmente após uma navegação drill-down ou ao retornar ao nível raiz.
   *
   * O método percorre todos os elementos <br-menu-list> e <br-menu-item> dentro do componente
   * e garante que estejam visíveis, removendo o atributo 'hidden' que pode ter sido
   * adicionado durante a navegação entre submenus.
   */
  private showAllMenuItems() {
    const allMenuLists = this.el.querySelectorAll('br-menu-list')
    const allMenuItems = this.el.querySelectorAll('br-menu-item')

    allMenuLists.forEach((menuList: Element) => {
      ;(menuList as HTMLElement).removeAttribute('hidden')
    })

    allMenuItems.forEach((menuItem: Element) => {
      ;(menuItem as HTMLElement).removeAttribute('hidden')
    })
  }

  /**
   * Marca ou desmarca um elemento <br-menu-list> como ativo no contexto de navegação drill-down.
   *
   * Quando 'active' é true, adiciona o atributo 'drill-active' ao elemento, indicando que
   * ele está no foco da navegação. Quando 'active' é false, remove esse atributo.
   *
   * Esse atributo pode ser utilizado para aplicar estilos visuais ou lógica condicional
   * durante a navegação entre níveis do menu.
   *
   * @param element Elemento <br-menu-list> a ser marcado/desmarcado
   * @param active Boolean indicando se o elemento deve ser marcado como ativo
   */
  private setDrillActive(element: HTMLElement, active: boolean) {
    if (active) {
      element.setAttribute('drill-active', '')
    } else {
      element.removeAttribute('drill-active')
    }
  }

  /**
   * Remove todos os marcadores de navegação drill-down do menu.
   *
   * Esta função percorre todos os elementos <br-menu-list> que possuem os atributos
   * 'drill-active' ou 'drill-ancestor' e remove esses atributos. Isso é necessário
   * para garantir que, ao retornar ao nível raiz ou reiniciar a navegação, nenhum
   * marcador visual ou lógico de navegação anterior permaneça ativo.
   */
  private clearAllDrillMarkers() {
    // Remove o atributo 'drill-active' de todos os <br-menu-list>
    this.el.querySelectorAll('br-menu-list[drill-active]').forEach((e) => {
      ;(e as HTMLElement).removeAttribute('drill-active')
    })
    // Remove o atributo 'drill-ancestor' de todos os <br-menu-list>
    this.el.querySelectorAll('br-menu-list[drill-ancestor]').forEach((e) => {
      ;(e as HTMLElement).removeAttribute('drill-ancestor')
    })
  }

  /**
   * Realiza a navegação de retorno (voltar) em menus do tipo drill-down.
   *
   * Esta função é chamada quando o usuário deseja voltar um nível na navegação do menu.
   * Ela utiliza uma pilha de navegação (navigationStack) para controlar o histórico de navegação.
   *
   * - Se houver elementos na pilha, remove o elemento do topo (nível atual) e desmarca como ativo.
   * - Se, após a remoção, a pilha estiver vazia, significa que o usuário retornou ao nível raiz:
   *   - Restaura o estado inicial do menu, mostrando todos os itens e limpando marcadores de navegação.
   * - Caso contrário, ativa o elemento do nível anterior e oculta os demais, mantendo o contexto do drill-down.
   */
  private goBack() {
    if (this.navigationStack.length > 0) {
      // Remove o elemento do topo da pilha (nível atual)
      const removed = this.navigationStack.pop()
      if (removed !== undefined) this.setDrillActive(removed.element, false)

      if (this.navigationStack.length === 0) {
        // Se a pilha está vazia, voltamos ao nível raiz do menu
        this.currentLevel = 0
        this.inSubmenu = false
        this.clearAllDrillMarkers()
        this.showAllMenuItems()
      } else {
        // Caso ainda haja elementos na pilha, voltamos ao nível anterior do drill-down
        const previousElement = this.navigationStack[this.navigationStack.length - 1].element
        const previousLevel = parseInt(previousElement.getAttribute('menu-level') || '0', 10)
        // Atualiza o nível atual para refletir o contexto correto
        this.currentLevel = previousLevel + 1
        // Marca o elemento anterior como ativo
        this.setDrillActive(previousElement, true)
        // Oculta todos os itens, exceto o submenu ativo
        this.hideAllExcept(previousElement)
      }
    }
  }

  private handleToggleMenu = () => {
    this.toggleMenu()
  }

  private handleGoBack = () => {
    this.goBack()
  }

  private handleCloseMenu = () => {
    this.closeMenu()
  }

  /**
   * Verifica se um slot específico possui conteúdo.
   */
  private hasSlotContent(slotName: string): boolean {
    return this.el.querySelector(`[slot="${slotName}"]`) !== null
  }

  private getEffectiveBreakpoints(): string {
    // Se o usuário definiu explicitamente, respeita o valor
    if (this.breakpoints && this.breakpoints.trim() !== '') return this.breakpoints.trim()
    // Não aplicar breakpoints para o modo contextual para evitar que ele se torne uma barra lateral em telas maiores.
    if (this.contextual) {
      return ''
    }
    // O padrão para menu flutuante é não ter breakpoints, a menos que o usuário especifique.
    return ''
  }

  private getCssClassMap(): CssClassMap {
    const effective = this.getEffectiveBreakpoints()
    return {
      'br-menu': true,
      active: this.active,
      push: this.push,
      contextual: this.contextual,
      large: this.density === 'large',
      small: this.density === 'small',
      ...(effective ? effective.split(' ').reduce((acc, bp) => ({ ...acc, [bp]: true }), {}) : {}),
    }
  }

  /**
   * Retorna as classes CSS para o painel do menu.
   * Aplica breakpoints apenas quando o menu NÃO está no modo push ou contextual.
   */
  private getMenuPanelClasses(): string {
    const baseClass = 'menu-panel'

    // No modo push ou contextual, não aplica breakpoints
    if (this.push || this.contextual) {
      return baseClass
    }

    // Nos outros modos, aplica os breakpoints responsivos
    const breakpoints = this.getEffectiveBreakpoints()
    return breakpoints ? `${baseClass} ${breakpoints}` : baseClass
  }

  render() {
    return (
      <Host>
        <div class={this.getCssClassMap()}>
          {!this.contextual && (
            <br-button
              id="toggle-menu"
              shape="circle"
              density="small"
              aria-label="Menu"
              onClick={this.handleToggleMenu}
            >
              <br-icon icon-name={`fa6-solid:bars`} aria-hidden="true"></br-icon>
            </br-button>
          )}

          {this.contextual && (
            <div class="menu-trigger">
              <br-button
                id="contextual-trigger"
                emphasis="primary"
                shape="block"
                aria-label={this.contextualLabel}
                aria-expanded={String(this.active)}
                aria-controls="contextual-menu-panel"
                onClick={this.handleToggleMenu}
              >
                <span class="trigger-label">{this.contextualLabel}</span>
                <br-icon
                  icon-name={`fa6-solid:chevron-${this.active ? 'down' : 'up'}`}
                  class="trigger-icon"
                  aria-hidden="true"
                ></br-icon>
              </br-button>
            </div>
          )}

          <div class={'menu-container'}>
            <div id={this.contextual ? 'contextual-menu-panel' : undefined} class={this.getMenuPanelClasses()}>
              {!this.contextual && this.hasSlotContent('header') && <slot name="header"></slot>}

              <nav class="menu-body">
                {this.inSubmenu && this.currentLevel > 0 && (
                  <div class="menu-back-button">
                    <button class="back-btn" onClick={this.handleGoBack} aria-label="Voltar ao nível anterior">
                      <br-icon class="back-icon" icon-name="fa6-solid:angle-left" aria-hidden="true"></br-icon>
                      <span class="back-title">
                        {this.navigationStack.length > 0
                          ? `${this.navigationStack[this.navigationStack.length - 1].label}`
                          : ''}
                      </span>
                      {this.navigationStack.length > 0 && (
                        <span class="current-icon" aria-hidden="true">
                          <br-icon
                            icon-name={`fa6-solid:${this.navigationStack[this.navigationStack.length - 1].element.getAttribute('icon') || 'bars'}`}
                          ></br-icon>
                        </span>
                      )}
                    </button>
                  </div>
                )}
                <slot></slot>
              </nav>

              {!this.contextual && (
                <div class="menu-footer" part="footer">
                  {this.hasSlotContent('logo') && (
                    <div class="menu-logos" part="logo">
                      <slot name="logo"></slot>
                    </div>
                  )}

                  {this.hasSlotContent('link') && (
                    <div class="menu-links" part="link">
                      <slot name="link"></slot>
                    </div>
                  )}

                  {this.hasSlotContent('social') && (
                    <div class="social-network" part="social">
                      {this.socialTitle && <div class="social-network-title">{this.socialTitle}</div>}
                      <div class="sharegroup">
                        <slot name="social"></slot>
                      </div>
                    </div>
                  )}

                  {this.hasSlotContent('info') && (
                    <div class="menu-info" part="info">
                      <slot name="info"></slot>
                    </div>
                  )}
                </div>
              )}
            </div>

            {this.active && <div class="menu-scrim" onClick={this.handleCloseMenu}></div>}
          </div>
        </div>
      </Host>
    )
  }
}
